import { useCallback, useEffect, useRef, useState } from 'react';
import { getUser } from '@/apis/experience/mfa';
import useApi from '@/hooks/use-api';

interface UserData {
  email: string;
  phone: string;
  availableFactors: string[];
}

interface CacheEntry {
  data: UserData;
  timestamp: number;
  userId: string;
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// In-memory cache for user data
const userDataCache = new Map<string, CacheEntry>();

// Track ongoing requests to prevent duplicate calls
const ongoingRequests = new Map<string, Promise<UserData>>();

/**
 * Hook to fetch and cache user data to prevent excessive API calls
 * and avoid rate limiting issues
 */
const useCachedUserData = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const asyncGetUser = useApi(getUser);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Clear expired cache entries
   */
  const clearExpiredCache = useCallback(() => {
    const now = Date.now();
    for (const [key, entry] of userDataCache.entries()) {
      if (now - entry.timestamp > CACHE_DURATION) {
        userDataCache.delete(key);
      }
    }
  }, []);

  /**
   * Fetch user data with caching and deduplication
   */
  const fetchUserData = useCallback(
    async (userId: string, skipInitInteraction = false): Promise<UserData | null> => {
      if (!userId) {
        setError(new Error('User ID is required'));
        return null;
      }

      // Clear any expired cache entries
      clearExpiredCache();

      // Check if we have valid cached data
      const cachedEntry = userDataCache.get(userId);
      if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_DURATION) {
        setUserData(cachedEntry.data);
        setError(null);
        return cachedEntry.data;
      }

      // Check if there's already an ongoing request for this user
      const ongoingRequest = ongoingRequests.get(userId);
      if (ongoingRequest) {
        try {
          const data = await ongoingRequest;
          setUserData(data);
          setError(null);
          return data;
        } catch (err) {
          setError(err instanceof Error ? err : new Error(String(err)));
          return null;
        }
      }

      // Create new request
      setIsLoading(true);
      setError(null);

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      const requestPromise = (async () => {
        try {
          const data = await getUser(userId, skipInitInteraction);
          
          // Cache the successful response
          const cacheEntry: CacheEntry = {
            data,
            timestamp: Date.now(),
            userId,
          };
          userDataCache.set(userId, cacheEntry);
          
          setUserData(data);
          return data;
        } catch (err) {
          // Check if it's a rate limit error
          if (err instanceof Error && err.message?.includes('Too many attempts')) {
            console.error('Rate limited! Waiting before retry...');
            // Store the error but don't throw immediately
            setError(err);
          }
          
          // Check if it's a session error or invalid user ID - navigate to error page
          const errorResponse = (err as any)?.response?.data || (err as any)?.data || {};
          const isSessionError = (
            err instanceof Error && (
              err.message?.includes('session not found') || 
              err.message?.includes('Session not found') ||
              err.message?.includes('interaction session id cookie not found') ||
              err.message?.includes('Internal server error') ||
              err.message?.includes('500')
            )
          ) || errorResponse?.code === 'session.not_found' ||
             errorResponse?.code === 'session.interaction_not_found' ||
             errorResponse?.error === 'invalid_request' ||
             errorResponse?.error_description?.includes('interaction session id cookie not found');

          if (isSessionError) {
            console.error('Session expired or invalid user ID! Redirecting to error page...');
            // Clear cache for this user to prevent further attempts
            userDataCache.delete(userId);
            ongoingRequests.delete(userId);
            // Navigate to error page instead of sign-in
            window.location.href = '/error?message=session_expired';
            return null;
          }
          
          throw err;
        } finally {
          setIsLoading(false);
          ongoingRequests.delete(userId);
        }
      })();

      // Track the ongoing request
      ongoingRequests.set(userId, requestPromise);

      try {
        return await requestPromise;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      }
    },
    [clearExpiredCache, asyncGetUser]
  );

  /**
   * Clear cache for a specific user or all users
   */
  const clearCache = useCallback((userId?: string) => {
    if (userId) {
      userDataCache.delete(userId);
      ongoingRequests.delete(userId);
    } else {
      userDataCache.clear();
      ongoingRequests.clear();
    }
  }, []);

  /**
   * Check if we're currently rate limited
   */
  const isRateLimited = useCallback(() => {
    return error?.message?.includes('Too many attempts') ?? false;
  }, [error]);

  /**
   * Get rate limit retry time from error message
   */
  const getRateLimitRetryTime = useCallback(() => {
    if (!error?.message) return null;
    
    const match = error?.message?.match(/in (\d+) minutes/);
    if (match?.[1]) {
      return parseInt(match[1], 10) * 60 * 1000; // Convert to milliseconds
    }
    return null;
  }, [error]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    userData,
    isLoading,
    error,
    fetchUserData,
    clearCache,
    isRateLimited,
    getRateLimitRetryTime,
  };
};

export default useCachedUserData;